import { ReactNode } from 'react'
import { FormControl, FormLabel, FormErrorMessage, HStack, Tooltip, Box, Input, Text } from '@chakra-ui/react'
import { get } from 'lodash'
import { useFormContext, Controller } from 'react-hook-form'
import NumberFormat, { FormatInputValueFunction, NumberFormatValues, SourceInfo } from 'react-number-format'
import Icon from 'components/Icon'
import { DATE_OF_BIRTH_PATTERN, DAY_PATTERN, MONTH_PATTERN, YEAR_PATTERN } from 'constants/common'

export interface IFormItemProps {
  id?: string
  name: string
  label: string
  type?: string
  format?: string | FormatInputValueFunction
  mask?: string | string[]
  placeholder?: string
  prefix?: string
  suffix?: string
  thousandSeparator?: boolean
  defaultValue?: number | string
  onValueChange?: (values: NumberFormatValues, sourceInfo: SourceInfo) => void
  isSmallLabel?: boolean
  isRequired?: boolean
  children?: ReactNode
  helperText?: string
  disabled?: boolean
  autoComplete?: string
  labelLineHeight?: number
  isInHStack?: boolean
  height?: string
}

const FormItemWithMask = (props: IFormItemProps) => {
  const {
    id,
    name,
    label,
    type,
    format,
    mask,
    placeholder,
    prefix,
    suffix,
    thousandSeparator,
    defaultValue,
    onValueChange,
    isSmallLabel = false,
    isRequired = true,
    children,
    helperText,
    disabled,
    autoComplete,
    labelLineHeight,
    isInHStack = false,
    height
  } = props
  const {
    control,
    formState: { errors }
  } = useFormContext()

  let pattern
  switch (name) {
    case 'ssn':
      //*INFO: validate ***-**-****
      pattern = {
        value: /^\d{3}-\d{2}-\d{4}$/,
        message: 'Please enter a valid SSN'
      }
      break
    case 'dateOfBirth':
      //*INFO: validate (MM/DD/YYYY), with a year between 1900 and 2099
      pattern = {
        value: DATE_OF_BIRTH_PATTERN,
        message: 'Please enter a valid date'
      }
      break
    case 'month':
      //*INFO: validate month: between 1 and 12
      pattern = {
        value: MONTH_PATTERN,
        message: 'Invalid month'
      }
      break
    case 'day':
      //*INFO: validate day: between 1 and 31
      pattern = {
        value: DAY_PATTERN,
        message: 'Invalid day'
      }
      break
    case 'year':
      //*INFO: validate year: between 1900 and 2099
      pattern = {
        value: YEAR_PATTERN,
        message: 'Invalid year'
      }
      break
    default:
      pattern = undefined
  }

  const isSsn: boolean = name === 'ssn'

  const disabledProps = disabled
    ? {
        disabled: true,
        background: 'gray.100',
        opacity: '0.7 !important',
        color: 'gray.400',
        variant: 'filled'
      }
    : {}
  return (
    <FormControl isInvalid={!!get(errors, name, false)}>
      <HStack
        marginTop={{ base: isSsn ? '18px' : 'unset', md: isSsn ? 2 : isInHStack ? 2 : 'unset' }}
        spacing="14px"
        maxHeight={6}
        marginBottom={{ base: isSsn ? '26px' : 'unset', md: isSsn ? 4 : 'unset' }}
        position="relative"
      >
        <FormLabel
          fontSize={isSmallLabel ? 'sm' : 'md'}
          color={isSmallLabel ? 'gray.500' : 'gray.700'}
          lineHeight={labelLineHeight ?? 5}
          marginBottom={isInHStack ? 3 : 0}
          marginInlineEnd={0}
        >
          {label}
          {name === 'ssn' && (
            <a href="mailto:leasing@livehomeroom.com">
              <HStack spacing={2} flexWrap="wrap">
                <Text whiteSpace={{ base: 'unset', md: 'nowrap' }}>
                  * For international SSN, please contact us at leasing@livehomeroom.com
                </Text>
              </HStack>
            </a>
          )}
        </FormLabel>
        {helperText && (
          <Tooltip label={helperText} hasArrow placement="right" background="gray.900" color="white" fontSize="sm">
            <Box textAlign="center" height="20px">
              <Icon iconName="information_line_blue.svg" size={20} />
            </Box>
          </Tooltip>
        )}
      </HStack>
      {children ? (
        children
      ) : (
        <Controller
          name={name}
          control={control}
          rules={{ required: isRequired ? `${label} is required` : false, pattern }}
          render={({ field: { onChange, onBlur, value } }) => (
            <Input
              height={height}
              id={id}
              as={NumberFormat}
              type={type}
              value={value}
              onBlur={onBlur}
              onChange={onChange}
              format={format}
              mask={mask}
              prefix={prefix}
              suffix={suffix}
              thousandSeparator={thousandSeparator}
              defaultValue={defaultValue}
              onValueChange={onValueChange}
              autoComplete={autoComplete ?? name}
              placeholder={placeholder}
              {...disabledProps}
            />
          )}
        />
      )}
      <FormErrorMessage textAlign="start">{get(errors, `${name}.message`, '')}</FormErrorMessage>
    </FormControl>
  )
}

export default FormItemWithMask
